import React, { createContext, useContext, useMemo, useCallback, useState } from 'react';
import {
  makeMove as makeMoveApi,
  getHint as getHintApi,
  setNotes as setNotesApi,
  deleteNotes as deleteNotesApi,
  undoMove as undoMoveApi,
  eraseCell as eraseCellApi,
  validatePuzzle as validatePuzzleApi,
  revealCell as revealCellApi,
  operationCell as operationCellApi,
  deleteCell as deleteCellApi,
  colorCell as colorCellApi,
  selectCell as selectCellApi,
} from '../services/gameApi';
import { createRevert as apiCreateRevert } from '../services/api.js';
import { useGlobalError } from './GlobalErrorContext';
import { useGameState } from './GameStateContext';
import { useUser } from './UserContext';
import { useMetadata } from './MetadataContext';
import { substituteColors } from '../utils/colorUtils';

const PuzzleStateContext = createContext(null);

// Note: This function will be enhanced by the provider to include color substitution
function mergePuzzleState(prevState, response, gameData) {
  // Use the most recent dynamic state, but fallback to previous or gameData for static fields
  const merged = {
    ...prevState,
    ...response,
    mode: response.mode || prevState?.mode || gameData?.mode,
    status: response.status || prevState?.status || gameData?.status,
    state: response.state || prevState?.state || gameData?.state,
    puzzle: response.puzzle !== null ? response.puzzle : prevState?.puzzle || gameData?.puzzle,
    grid: response.grid !== null ? response.grid : prevState?.grid || gameData?.grid,
    stats: response.stats !== null ? response.stats : prevState?.stats || gameData?.stats,
    messages: response.messages !== null ? response.messages : prevState?.messages || gameData?.messages,
  };
  
  // Sync cell_notes from backend state to grid cells' footer_text
  if (merged.grid?.cells) {
    merged.grid = {
      ...merged.grid,
      cells: merged.grid.cells.map(cell => {
        const notesKey = `${cell.x},${cell.y}`;
        // Update footer_text based on cell_notes from backend
        if (merged.state?.cell_notes?.[notesKey]) {
          return {
            ...cell,
            footer_text: merged.state.cell_notes[notesKey]
          };
        } else if (cell.value === 0 || cell.value === null) {
          // Clear footer_text if no notes exist for empty cells
          return {
            ...cell,
            footer_text: []
          };
        }
        return cell;
      })
    };
  }
  
  return merged;
}

export const PuzzleStateProvider = ({ children }) => {
  const [puzzleState, setPuzzleState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingOperation, setLoadingOperation] = useState(null);
  const [optimisticGrid, setOptimisticGrid] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [error, setError] = useState(null);
  const [isNoteMode, setIsNoteMode] = useState(false);
  
  const { triggerError } = useGlobalError();
  const { activeMode, gameData } = useGameState();
  const { userData } = useUser();
  const { getThemePalettes, getFixedColors } = useMetadata();

  // Function to apply color substitution to game data
  const applyColorSubstitution = useCallback((data) => {
    if (!data || !userData?.theme) {
      return data;
    }

    const themePalettes = getThemePalettes();
    const fixedColors = getFixedColors();
    
    return substituteColors(
      data, 
      userData.theme, 
      userData.lightMode, 
      themePalettes, 
      fixedColors
    );
  }, [userData?.theme, userData?.lightMode, getThemePalettes, getFixedColors]);

  // Color-aware merge function that applies color substitution to merged result
  const mergePuzzleStateWithColors = useCallback((prevState, response, gameData) => {
    const merged = mergePuzzleState(prevState, response, gameData);
    return applyColorSubstitution(merged) || merged;
  }, [applyColorSubstitution]);
  
  React.useEffect(() => {

    if (gameData?.status === 'active') {
      // Use mergePuzzleState to sync cell_notes to footer_text, then apply color substitution
      const mergedGameData = mergePuzzleState(null, gameData, null);
      const gameDataWithColors = applyColorSubstitution(mergedGameData);
      setPuzzleState(gameDataWithColors);
      setOptimisticGrid(null); // Clear optimistic updates when new state arrives
    } else {
      setPuzzleState(null);
      setOptimisticGrid(null);
      setSelectedCell(null);
    }
  }, [gameData, applyColorSubstitution]);

  // Helper function to create optimistic grid update
  const createOptimisticUpdate = useCallback((x, y, value) => {
    const currentGrid = optimisticGrid || puzzleState?.grid;
    if (!currentGrid) return null;

    const updatedGrid = {
      ...currentGrid,
      cells: currentGrid.cells.map(cell => {
        if (cell.x === x && cell.y === y) {
          return {
            ...cell,
            value: value,
            is_given: false,
            footer_text: value !== 0 ? [] : cell.footer_text // Clear notes when setting value
          };
        }
        return cell;
      })
    };

    // Apply color substitution to optimistic update
    return applyColorSubstitution(updatedGrid) || updatedGrid;
  }, [optimisticGrid, puzzleState, applyColorSubstitution]);

  // Helper function for optimistic notes update
  const createOptimisticNotesUpdate = useCallback((x, y, notes) => {
    const currentGrid = optimisticGrid || puzzleState?.grid;
    if (!currentGrid) return null;

    const updatedGrid = {
      ...currentGrid,
      cells: currentGrid.cells.map(cell => {
        if (cell.x === x && cell.y === y) {
          return {
            ...cell,
            footer_text: notes,
            value: 0  // Clear value when setting notes
          };
        }
        return cell;
      })
    };

    // Apply color substitution to optimistic update
    return applyColorSubstitution(updatedGrid) || updatedGrid;
  }, [optimisticGrid, puzzleState, applyColorSubstitution]);

  const makeMove = useCallback(async (x, y, value) => {
    if (!activeMode || !puzzleState) return;
    
    // Create optimistic update
    const valueToSend = value === "0" ? null : value.toString();
    const numValue = value === "0" ? 0 : parseInt(value);
    const updatedGrid = createOptimisticUpdate(x, y, numValue);
    
    setOptimisticGrid(updatedGrid);
    setSelectedCell(null); // Clear selection after move
    setIsLoading(true);
    setLoadingOperation('move');
    setError(null);
    
    try {
      const response = await makeMoveApi(activeMode, x, y, valueToSend);
      
      // Merge response with existing puzzle state instead of replacing it
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      setOptimisticGrid(null); // Clear optimistic update
      setIsLoading(false);
      return response;
    } catch (error) {
      setError('Failed to make move. Please try again.');
      setIsLoading(false);
      // Revert optimistic update after delay
      setTimeout(() => {
        setOptimisticGrid(null);
      }, 2000);
      
      triggerError({
        type: 'non-critical',
        message: `Failed to make move in ${activeMode} mode`,
        error
      });
      throw error;
    } finally {
      setLoadingOperation(null);
    }
  }, [activeMode, gameData, puzzleState, triggerError, createOptimisticUpdate, mergePuzzleStateWithColors]);

  const getHint = useCallback(async () => {
    if (!activeMode || !puzzleState) return;
    setIsLoading(true);
    setLoadingOperation('hint');
    setError(null);
    try {
      const response = await getHintApi(activeMode);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      setIsLoading(false);
      setLoadingOperation(null);
      return response;
    } catch (error) {
      setIsLoading(false);
      setLoadingOperation(null);
      // Special handling: no hints available is non-critical and does not
      // throw. Only that error code: any other error (ERR_GAME_STATE_BUSY, …)
      // takes the normal error path
      const headers = error?.response?.headers || {};
      const errorCode = headers['x-error-code'] || headers['X-Error-Code'];
      if (errorCode === 'ERR_NO_HINTS_AVAILABLE') {
        triggerError({
          type: 'non-critical',
          message: 'No hints available for this puzzle.',
          error
        });
        return null;
      }
      // Otherwise, treat as a normal non-critical error
      triggerError({
        type: 'non-critical',
        message: `Failed to get hint in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const setNotes = useCallback(async (x, y, value) => {
    if (!activeMode || !puzzleState) return;
    
    // For optimistic update, we need to determine if we're adding or removing the note
    const currentGrid = optimisticGrid || puzzleState?.grid;
    const currentCell = currentGrid?.cells?.find(cell => cell.x === x && cell.y === y);
    const currentNotes = currentCell?.footer_text || [];
    
    // Toggle logic for optimistic update
    let newNotes;
    if (currentNotes.includes(value)) {
      // Remove the note
      newNotes = currentNotes.filter(note => note !== value);
    } else {
      // Add the note
      newNotes = [...currentNotes, value];
    }
    
    // Create optimistic notes update with the toggled notes
    const updatedGrid = createOptimisticNotesUpdate(x, y, newNotes);
    setOptimisticGrid(updatedGrid);
    
    try {
      const response = await setNotesApi(activeMode, x, y, value);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      setOptimisticGrid(null); // Clear optimistic update
      return response;
    } catch (error) {
      setError('Failed to set notes. Please try again.');
      setIsLoading(false);
      // Revert optimistic update after delay
      setTimeout(() => {
        setOptimisticGrid(null);
      }, 2000);
      
      triggerError({
        type: 'non-critical',
        message: `Failed to set notes in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, optimisticGrid, triggerError, createOptimisticNotesUpdate, mergePuzzleStateWithColors]);

  const deleteAllNotes = useCallback(async (x, y) => {
    if (!activeMode || !puzzleState) return;
    
    // Create optimistic update clearing all notes
    const updatedGrid = createOptimisticNotesUpdate(x, y, []);
    setOptimisticGrid(updatedGrid);
    
    try {
      const response = await deleteNotesApi(activeMode, x, y);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      setOptimisticGrid(null); // Clear optimistic update
      return response;
    } catch (error) {
      setError('Failed to delete notes. Please try again.');
      setIsLoading(false);
      // Revert optimistic update after delay
      setTimeout(() => {
        setOptimisticGrid(null);
      }, 2000);
      
      triggerError({
        type: 'non-critical',
        message: `Failed to delete notes in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, createOptimisticNotesUpdate, mergePuzzleStateWithColors]);

  const undoMove = useCallback(async () => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await undoMoveApi(activeMode);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to undo move in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const eraseCell = useCallback(async (x, y) => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await eraseCellApi(activeMode, x, y);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to erase cell in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const validatePuzzle = useCallback(async () => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await validatePuzzleApi(activeMode);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to validate puzzle in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const createRevert = useCallback(async () => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await apiCreateRevert();
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to revert stage in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const revealCell = useCallback(async () => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await revealCellApi(activeMode);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to reveal cell in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const operationCell = useCallback(async (operation) => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await operationCellApi(activeMode, operation);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to apply operation in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const deleteCell = useCallback(async () => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await deleteCellApi(activeMode);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to delete cell in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const colorCell = useCallback(async (color) => {
    console.log('colorCell called with:', { color, activeMode, puzzleState: !!puzzleState });
    if (!activeMode || !puzzleState) {
      console.log('colorCell early return - missing activeMode or puzzleState');
      return;
    }
    
    try {
      console.log('Making colorCellApi call...');
      const response = await colorCellApi(activeMode, color);
      console.log('colorCellApi response:', response);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      console.error('colorCell error:', error);
      triggerError({
        type: 'non-critical',
        message: `Failed to color cell in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);

  const selectCell = useCallback(async (x, y) => {
    if (!activeMode || !puzzleState) return;
    
    try {
      const response = await selectCellApi(activeMode, x, y);
      setPuzzleState(prev => mergePuzzleStateWithColors(prev, response, gameData));
      return response;
    } catch (error) {
      triggerError({
        type: 'non-critical',
        message: `Failed to select cell in ${activeMode} mode`,
        error
      });
      throw error;
    }
  }, [activeMode, gameData, puzzleState, triggerError, mergePuzzleStateWithColors]);


  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    return {
      puzzleState,
      isLoading,
      loadingOperation,
      optimisticGrid,
      selectedCell,
      error,
      makeMove,
      getHint,
      setNotes,
      deleteAllNotes,
      undoMove,
      eraseCell,
      setSelectedCell,
      validatePuzzle,
      revealCell,
      operationCell,
      deleteCell,
      colorCell,
      selectCell,
      createRevert,
      isNoteMode,
      setIsNoteMode,
    };
  }, [puzzleState, isLoading, loadingOperation, optimisticGrid, selectedCell, error, makeMove, getHint, setNotes, deleteAllNotes, undoMove, eraseCell, validatePuzzle, revealCell, operationCell, deleteCell, colorCell, selectCell, createRevert, isNoteMode]);

  return (
    <PuzzleStateContext.Provider value={contextValue}>
      {children}
    </PuzzleStateContext.Provider>
  );
};

export const usePuzzleState = () => {
  const context = useContext(PuzzleStateContext);
  if (!context) {
    throw new Error('usePuzzleState must be used within a PuzzleStateProvider');
  }
  return context;
};